import chroma from 'chroma-js';

import {
  random,
  normalize,
  getYValueForBezier,
  range,
  generateId,
  roundTo,
  sampleOne,
} from '@/utils';

export interface Color {
  hue: number;
  saturation: number;
  lightness: number;
}

export interface ColorWithId {
  id: string;
  color: Color | null;
}

export interface XYPair {
  x: number;
  y: number;
}
export type EasingCurve = [XYPair, XYPair];

export interface QueryParams {
  colors?: string;
  colorMode?: ColorMode;
  precision?: string;
  angle?: string;
  easingCurve?: string;
}

export const MIN_PRECISION = 1;
export const MAX_PRECISION = 20;

export const MAXIMUM_NUM_OF_COLORS = 5;

export const VALID_COLOR_MODES = [
  'lrgb',
  'hsl',
  'hsv',
  'hcl',
  'lab',
] as const;

export type ColorMode = (typeof VALID_COLOR_MODES)[number];

interface CurvePreset {
  id: string;
  label: string;
  curve: EasingCurve;
}
export const CURVE_PRESETS: Array<CurvePreset> = [
  {
    id: 'linear',
    label: 'Linear',
    curve: [
      { x: 0.25, y: 0.75 },
      { x: 0.75, y: 0.25 },
    ],
  },
  {
    id: 'ease',
    label: 'Ease',
    curve: [
      { x: 0.333, y: 1 },
      { x: 0.666, y: 0 },
    ],
  },
  {
    id: 'fun',
    label: 'Fun',
    curve: [
      { x: 1.1, y: 1 },
      { x: -0.1, y: 0 },
    ],
  },
];

export function generateGradient({
  colors,
  angle,
  colorMode,
  easingCurve,
  precision,
}: {
  colors: Array<ColorWithId>;
  angle: number;
  colorMode: ColorMode;
  easingCurve: EasingCurve;
  precision: number;
}) {
  const numOfStops = colors.length + Math.round(precision);

  const baseColors = colors
    .filter((c) => c.color)
    .map((c) => c.color) as Array<Color>;

  // Convert to Chroma
  const chromaColors = baseColors.map((color) => {
    return chroma(
      color.hue,
      color.saturation,
      color.lightness,
      'hsl'
    );
  });

  const stops = chroma
    .scale(chromaColors)
    .mode(colorMode)
    .colors(numOfStops, 'hsl');

  // Apply this easing curve to these colors
  const formattedStops = stops.map((color, index) => {
    // For each value of X, running across the bottom axis,
    // we want to see how far away the bezier line is.
    const xTarget = index / (stops.length - 1);
    let y = getYValueForBezier(
      xTarget,
      1 - easingCurve[0].y,
      easingCurve[0].x,
      1 - easingCurve[1].y,
      easingCurve[1].x
    );

    y = Math.round(normalize(y, 0, 1, 0, 100));

    const hue = isNaN(color[0]) ? 0 : Math.round(color[0]);

    const hslString = `hsl(${hue}deg ${Math.round(
      color[1] * 100
    )}% ${Math.round(color[2] * 100)}%)`;

    return `${hslString} ${y}%`;
  });

  return `linear-gradient(
    ${angle}deg,
    ${formattedStops.join(',\n    ')}
  )`;
}

export function generateCodeSnippet(gradientString: string) {
  return `\
.gradient {
  background-image: ${gradientString};
}`;
}

export function generateHSLString(color: Color) {
  const sat = roundTo(color.saturation * 100, 2);
  const lit = roundTo(color.lightness * 100, 2);

  return `hsl(${color.hue}deg ${sat}% ${lit}%)`;
}

function generateColorsArray(
  colors: Array<string | Color>
): Array<ColorWithId> {
  const hslColors = colors.map((color) => {
    if (typeof color === 'string') {
      // If it's a string, it must be a hex code.
      const chromaColor = chroma(color);
      const hue = chromaColor.get('hsl.h');
      return {
        hue: isNaN(hue) ? 0 : hue,
        saturation: chromaColor.get('hsl.s'),
        lightness: chromaColor.get('hsl.l'),
      };
    } else {
      return color;
    }
  });

  const prepopulatedColors = hslColors.map((color) => ({
    color,
    id: generateId(),
  }));

  const remainingColors =
    MAXIMUM_NUM_OF_COLORS - prepopulatedColors.length;

  return [
    ...prepopulatedColors,
    ...range(remainingColors).map(() => ({
      color: null,
      id: generateId(),
    })),
  ];
}

// I'm feeling *very* lazy. Not bothering with any TS stuff.
export function encodeUrlString({
  colors,
  angle,
  colorMode,
  precision,
  easingCurve,
}: any) {
  let output = '';

  const colorHexCodes = colors
    .filter((c: any) => c.color)
    .map((c: any) => {
      const chromaColor = chroma(
        c.color.hue,
        c.color.saturation,
        c.color.lightness,
        'hsl'
      );

      return chromaColor.hex().replace('#', '');
    });

  output = `colors=${colorHexCodes.join('|')}`;
  output = `${output}&angle=${angle}`;
  output = `${output}&colorMode=${colorMode}`;
  output = `${output}&precision=${precision}`;

  const easingString = `${easingCurve[0].x}|${easingCurve[0].y}|${easingCurve[1].x}|${easingCurve[1].y}`;
  output = `${output}&easingCurve=${easingString}`;

  return output;
}

export function getInitialValues(query: QueryParams = {}) {
  const initialColors = generateColorsArray(
    query.colors
      ? query.colors.split('|')
      : [
          {
            hue: 240,
            saturation: 1,
            lightness: 0.2,
          },
          {
            hue: 55,
            saturation: 1,
            lightness: 0.5,
          },
        ]
  );

  const initialColorMode: ColorMode = query.colorMode ?? 'hcl';
  const initialPrecision = Number(query.precision ?? 8);
  const initialAngle = Number(query.angle ?? 45);

  let initialEasingCurve: EasingCurve = [
    { x: 0.25, y: 0.75 },
    { x: 0.75, y: 0.25 },
  ];

  if (query.easingCurve) {
    const queryValues = query.easingCurve.split('|');
    initialEasingCurve = [
      { x: Number(queryValues[0]), y: Number(queryValues[1]) },
      { x: Number(queryValues[2]), y: Number(queryValues[3]) },
    ];
  }

  return {
    initialColors,
    initialColorMode,
    initialPrecision,
    initialAngle,
    initialEasingCurve,
  };
}

export function getRandomColorPalette(): Array<ColorWithId> {
  const rawColors = sampleOne([
    [
      {
        hue: 240,
        saturation: 1,
        lightness: 0.5,
      },
      {
        hue: 200,
        saturation: 0.54,
        lightness: 0.9,
      },
    ],
    [
      {
        hue: 300,
        saturation: 1,
        lightness: 0.1,
      },
      {
        hue: 150,
        saturation: 0.44,
        lightness: 0.72,
      },
      {
        hue: 60,
        saturation: 1,
        lightness: 0.5,
      },
    ],
    [
      {
        hue: 0,
        saturation: 1,
        lightness: 0.49,
      },
      {
        hue: 53,
        saturation: 1,
        lightness: 0.9,
      },
      {
        hue: 30,
        saturation: 1,
        lightness: 0.5,
      },
      {
        hue: 327,
        saturation: 0.6,
        lightness: 0.2,
      },
    ],
    [
      {
        hue: 270,
        saturation: 1,
        lightness: 0.07,
      },
      {
        hue: 105,
        saturation: 1,
        lightness: 0.95,
      },
    ],
    [
      {
        hue: 315,
        saturation: 0.88,
        lightness: 0.57,
      },
      {
        hue: 251,
        saturation: 0.87,
        lightness: 0.76,
      },
      {
        hue: 256,
        saturation: 0,
        lightness: 0,
      },
    ],
    [
      {
        hue: 315,
        saturation: 0.73,
        lightness: 0.1,
      },
      {
        hue: 17,
        saturation: 0.95,
        lightness: 0.5,
      },
      {
        hue: 50,
        saturation: 1,
        lightness: 0.7,
      },
    ],
    [
      {
        hue: 305,
        saturation: 1,
        lightness: 0.35,
      },
      {
        hue: 0,
        saturation: 1,
        lightness: 0.9,
      },
    ],
    [
      {
        hue: 49,
        saturation: 1,
        lightness: 0.69,
      },
      {
        hue: 259,
        saturation: 1,
        lightness: 0.15,
      },
      {
        hue: 183,
        saturation: 0.79,
        lightness: 0.36,
      },
    ],
    [
      {
        hue: 345,
        saturation: 1,
        lightness: 0.42,
      },
      {
        hue: 74,
        saturation: 0.71,
        lightness: 0.56,
      },
      {
        hue: 175,
        saturation: 1,
        lightness: 0.35,
      },
      {
        hue: 235,
        saturation: 0.79,
        lightness: 0.38,
      },
      {
        hue: 267,
        saturation: 1,
        lightness: 0.07,
      },
    ],
    [
      {
        hue: 267,
        saturation: 1,
        lightness: 0.07,
      },
      {
        hue: 0,
        saturation: 0.72,
        lightness: 0.48,
      },
      {
        hue: 174,
        saturation: 1,
        lightness: 0.96,
      },
      {
        hue: 231,
        saturation: 0.62,
        lightness: 0.46,
      },
      {
        hue: 235,
        saturation: 0.68,
        lightness: 0.04,
      },
    ],
    [
      {
        hue: 314,
        saturation: 1,
        lightness: 0.05,
      },
      {
        hue: 0,
        saturation: 1,
        lightness: 0.5,
      },
      {
        hue: 293,
        saturation: 1,
        lightness: 0.97,
      },
    ],
    [
      {
        hue: 0,
        saturation: 0,
        lightness: 0,
      },
      {
        hue: 0,
        saturation: 0,
        lightness: 0,
      },
      {
        hue: 0,
        saturation: 0.11,
        lightness: 0.25,
      },
      {
        hue: 35,
        saturation: 0.08,
        lightness: 0.62,
      },
      {
        hue: 50,
        saturation: 0.83,
        lightness: 0.9,
      },
    ],
    [
      {
        hue: 223,
        saturation: 0.68,
        lightness: 0.06,
      },
      {
        hue: 266,
        saturation: 1,
        lightness: 0.5,
      },
      {
        hue: 270,
        saturation: 0.27,
        lightness: 0.94,
      },
    ],
    [
      {
        hue: 0,
        saturation: 0,
        lightness: 1,
      },
      {
        hue: 57,
        saturation: 1,
        lightness: 0.6,
      },
      {
        hue: 210,
        saturation: 0.74,
        lightness: 0.35,
      },
      {
        hue: 253,
        saturation: 0.19,
        lightness: 0.09,
      },
    ],
    [
      {
        hue: 60,
        saturation: 1,
        lightness: 0.5,
      },
      {
        hue: 240,
        saturation: 1,
        lightness: 0.5,
      },
    ],
  ]);

  return generateColorsArray(rawColors);
}

export function getRandomColorMode(): ColorMode {
  return sampleOne(VALID_COLOR_MODES);
}

export function getRandomPrecision() {
  return random(MIN_PRECISION, MAX_PRECISION);
}
export function getRandomAngle() {
  return sampleOne([
    0,
    0,
    10,
    35,
    40,
    45,
    45,
    45,
    90,
    90,
    90,
    90,
    100,
    120,
    180,
    175,
    random(0, 360),
    random(0, 360),
    random(0, 360),
    random(0, 360),
    random(0, 360),
    random(0, 360),
    random(0, 360),
    random(0, 360),
    random(0, 360),
    random(0, 360),
    random(0, 360),
  ]);
}

export function getRandomCurve(): EasingCurve {
  const presets = CURVE_PRESETS.map((preset) => preset.curve);

  return sampleOne([
    ...presets,
    [
      { x: 0.25, y: 0.75 },
      { x: 0.75, y: 0.25 },
    ],
    [
      { x: 0.25, y: 0.75 },
      { x: 0.75, y: 0.25 },
    ],
    [
      { x: 0.25, y: 0.75 },
      { x: 0.75, y: 0.25 },
    ],
    [
      { x: 0.25, y: 0.75 },
      { x: 0.75, y: 0.25 },
    ],
    [
      { x: 0.75, y: 0.9 },
      { x: 0, y: 0.1 },
    ],
    [
      { x: 0.75, y: 1 },
      { x: 1, y: 0.75 },
    ],
    [
      { x: 0, y: 0.25 },
      { x: 1, y: 0.75 },
    ],
    [
      { x: 0, y: 0.25 },
      { x: 0.25, y: 0 },
    ],
    [
      { x: 1.25, y: -0.25 },
      { x: -0.1, y: -0.1 },
    ],
    [
      { x: 1, y: 1 },
      { x: 0, y: 1 },
    ],
    [
      { x: 0.33, y: 1 },
      { x: -0.1, y: -0.1 },
    ],
    [
      { x: Math.random(), y: Math.random() },
      { x: Math.random(), y: Math.random() },
    ],
  ]);
}
